import {ChangeEvent, FormEvent, useEffect, useRef, useState} from 'react'
import {Navigate} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  FolderOpen,
  Loader2,
  RefreshCw,
  Search,
  Star,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import {PageTitle} from '../../../_metronic/layout/core'
import {GetPermissions} from '../../modules/auth'
import {
  clearFechamentoAtivo,
  downloadBlob,
  Fechamento,
  FECHAMENTOS_PERMISSION,
  listFechamentos,
  loadFechamentoPdf,
  MAX_PDF_SIZE,
  setFechamentoAtivo,
  uploadFechamento,
} from './fechamentosApi'
import FechamentoCover from './FechamentoCover'
import FechamentoViewer from './FechamentoViewer'
import './fechamentos.css'

const formatSize = (size: number) =>
  (size / 1024 / 1024).toLocaleString('pt-BR', {maximumFractionDigits: 1}) + ' MB'
const formatDate = (value: string) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR')
}

function FechamentosContent() {
  const [items, setItems] = useState<Fechamento[]>([])
  const [active, setActive] = useState<Fechamento>(null)
  const [activatingId, setActivatingId] = useState('')
  const [makeActive, setMakeActive] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [success, setSuccess] = useState('')
  const [file, setFile] = useState<File>(null)
  const [title, setTitle] = useState('')
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [offset, setOffset] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [viewer, setViewer] = useState<Fechamento>(null)
  const [downloadingId, setDownloadingId] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)
  const dropRef = useRef<(files: FileList) => void>(null)

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearchTerm(search.trim()), 300)
    return () => window.clearTimeout(timeout)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setListError('')
    listFechamentos(searchTerm, offset, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        setItems(data.items)
        setTotal(data.total)
        setActive(data.active ?? null)
        setLoading(false)
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setListError(error.message)
        setLoading(false)
      })
    return () => controller.abort()
  }, [searchTerm, offset, refresh])

  const resetFile = () => {
    setFile(null)
    setTitle('')
    if (input.current) input.current.value = ''
  }

  const selectFile = (files: FileList | File[]) => {
    const list = Array.from(files || [])
    setUploadError('')
    setSuccess('')
    if (!list.length) return
    if (list.length > 1) {
      setUploadError('Envie um PDF por vez.')
      return
    }
    const [selected] = list
    if (!/\.pdf$/i.test(selected.name)) {
      setUploadError('Selecione um arquivo PDF.')
      if (input.current) input.current.value = ''
      return
    }
    if (!selected.size || selected.size > MAX_PDF_SIZE) {
      setUploadError('O PDF deve ter conteúdo e tamanho de até 30 MB.')
      if (input.current) input.current.value = ''
      return
    }
    setFile(selected)
    setTitle(selected.name.replace(/\.pdf$/i, '').slice(0, 180))
    setMakeActive(!active)
  }

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => selectFile(event.target.files)
  dropRef.current = (files) => {
    if (!uploading) selectFile(files)
  }

  // Aceita soltar o PDF em qualquer ponto da página, não só dentro da área de envio.
  useEffect(() => {
    const hasFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types || []).includes('Files')
    const enter = (event: DragEvent) => {
      if (!hasFiles(event)) return
      dragDepth.current += 1
      setDragging(true)
    }
    const leave = (event: DragEvent) => {
      if (!hasFiles(event)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (!dragDepth.current) setDragging(false)
    }
    const over = (event: DragEvent) => {
      if (hasFiles(event)) event.preventDefault()
    }
    const drop = (event: DragEvent) => {
      if (!hasFiles(event)) return
      event.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      dropRef.current?.(event.dataTransfer.files)
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragleave', leave)
    window.addEventListener('dragover', over)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('dragover', over)
      window.removeEventListener('drop', drop)
    }
  }, [])

  const markActive = (closing: Fechamento | null) => {
    setActive(closing ? {...closing, isActive: true} : null)
    setItems((current) => current.map((item) => ({...item, isActive: item.id === closing?.id})))
  }

  const activate = async (closing: Fechamento) => {
    setActivatingId(closing.id)
    setListError('')
    setSuccess('')
    try {
      await setFechamentoAtivo(closing.id)
      markActive(closing)
    } catch (error) {
      setListError(error.message)
    } finally {
      setActivatingId('')
    }
  }

  const deactivate = async () => {
    setActivatingId(active.id)
    setListError('')
    setSuccess('')
    try {
      await clearFechamentoAtivo()
      markActive(null)
    } catch (error) {
      setListError(error.message)
    } finally {
      setActivatingId('')
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!file || uploading) return
    setUploading(true)
    setUploadError('')
    setSuccess('')
    setProgress(0)
    try {
      const result = await uploadFechamento(file, title, setProgress)
      let message = 'Fechamento salvo. ' + result.pageCount + ' páginas convertidas em imagens.'
      if (makeActive) {
        try {
          await setFechamentoAtivo(result.id)
          markActive(result)
          message += ' Definido como fechamento ativo.'
        } catch (error) {
          setUploadError(
            'O fechamento foi salvo, mas não foi definido como ativo: ' + error.message
          )
        }
      }
      setSuccess(message)
      resetFile()
      setSearch('')
      setSearchTerm('')
      setOffset(0)
      setRefresh((value) => value + 1)
      setViewer(result)
    } catch (error) {
      setUploadError(error.message)
    } finally {
      setUploading(false)
    }
  }

  const downloadPdf = async (closing: Fechamento) => {
    setDownloadingId(closing.id)
    setListError('')
    try {
      downloadBlob(await loadFechamentoPdf(closing.id), closing.fileName)
    } catch (error) {
      setListError(error.message)
    } finally {
      setDownloadingId('')
    }
  }

  return (
    <div className='fechamentos-page'>
      <PageTitle>Fechamentos</PageTitle>
      <header className='fechamentos-heading'>
        <span className='fechamentos-eyebrow'>ADMINISTRATIVO</span>
        <h1>Fechamentos</h1>
        <p>Centralize os relatórios de fechamento e visualize cada página em alta qualidade.</p>
      </header>
      <section className='fechamentos-upload' aria-labelledby='fechamentos-upload-title'>
        <div className='fechamentos-upload-heading'>
          <span className='fechamentos-upload-icon'>
            <UploadCloud size={25} />
          </span>
          <div>
            <h2 id='fechamentos-upload-title'>Novo fechamento</h2>
            <p>Cada página do PDF vira uma imagem em alta qualidade para apresentação.</p>
          </div>
        </div>
        <form onSubmit={submit}>
          <input
            ref={input}
            id='fechamentos-file'
            className='fechamentos-file-input'
            type='file'
            accept='.pdf,application/pdf'
            onChange={chooseFile}
            disabled={uploading}
            aria-label='Arquivo PDF'
            tabIndex={-1}
          />
          {!file ? (
            <button
              type='button'
              className={'fechamentos-dropzone' + (dragging ? ' dragging' : '')}
              onClick={() => input.current?.click()}
              aria-describedby='fechamentos-file-help'
            >
              <span className='fechamentos-dropzone-icon'>
                <UploadCloud size={28} />
              </span>
              <strong>
                {dragging
                  ? 'Solte o PDF para selecionar'
                  : 'Arraste o PDF aqui ou clique para escolher'}
              </strong>
              <small id='fechamentos-file-help'>
                Um arquivo por vez · até 30 MB e 100 páginas · sem senha
              </small>
            </button>
          ) : (
            <div className={'fechamentos-selected' + (dragging ? ' dragging' : '')}>
              <div className='fechamentos-selected-file'>
                <span className='fechamentos-selected-icon'>
                  <FileText size={22} />
                </span>
                <div className='fechamentos-selected-info'>
                  <strong title={file.name}>{file.name}</strong>
                  <small>
                    {dragging ? 'Solte para trocar o arquivo' : formatSize(file.size) + ' · PDF'}
                  </small>
                </div>
                {!uploading && (
                  <div className='fechamentos-selected-actions'>
                    <button
                      type='button'
                      className='fechamentos-button secondary'
                      onClick={() => input.current?.click()}
                    >
                      <RefreshCw size={15} /> Trocar
                    </button>
                    <button
                      type='button'
                      className='fechamentos-button secondary'
                      onClick={() => {
                        resetFile()
                        setUploadError('')
                      }}
                      aria-label='Remover arquivo selecionado'
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>
              <div className='fechamentos-upload-fields'>
                <div className='fechamentos-field'>
                  <label htmlFor='fechamentos-title'>Título do fechamento</label>
                  <input
                    id='fechamentos-title'
                    type='text'
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    maxLength={180}
                    placeholder='Ex.: Fechamento D&AI Setembro 2026'
                    disabled={uploading}
                  />
                </div>
                <label className='fechamentos-check'>
                  <input
                    type='checkbox'
                    checked={makeActive}
                    onChange={(event) => setMakeActive(event.target.checked)}
                    disabled={uploading}
                  />
                  Definir como fechamento ativo
                </label>
                <button
                  type='submit'
                  className='fechamentos-button primary'
                  disabled={!file || uploading}
                >
                  {uploading ? (
                    <Loader2 size={18} className='fechamentos-spin' />
                  ) : (
                    <UploadCloud size={18} />
                  )}
                  {uploading ? 'Processando...' : 'Enviar PDF'}
                </button>
              </div>
              {uploading && (
                <div className='fechamentos-progress' role='status' aria-live='polite'>
                  <div>
                    <span style={{width: progress + '%'}} />
                  </div>
                  <p>
                    {progress < 100
                      ? 'Enviando PDF: ' + progress + '%'
                      : 'Convertendo páginas em imagens. Aguarde a conclusão.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </form>
        {uploadError && (
          <div className='fechamentos-message error' role='alert'>
            <AlertCircle size={18} />
            {uploadError}
          </div>
        )}
        {success && (
          <div className='fechamentos-message success' role='status'>
            <CheckCircle2 size={18} />
            {success}
          </div>
        )}
      </section>
      <section className='fechamentos-active' aria-labelledby='fechamentos-active-title'>
        <h2 id='fechamentos-active-title'>
          <Star size={16} /> Fechamento ativo
        </h2>
        {active ? (
          <div className='fechamentos-active-card'>
            <button
              type='button'
              className='fechamentos-active-cover'
              onClick={() => setViewer(active)}
              aria-label={'Visualizar ' + active.title}
            >
              <FechamentoCover closing={active} />
            </button>
            <div className='fechamentos-active-info'>
              <h3 title={active.title}>{active.title}</h3>
              <p>
                {active.pageCount} {active.pageCount === 1 ? 'página' : 'páginas'} ·{' '}
                {formatDate(active.createdAt)} · Enviado por {active.createdByName}
              </p>
            </div>
            <div className='fechamentos-active-actions'>
              <button
                type='button'
                className='fechamentos-button primary'
                onClick={() => setViewer(active)}
              >
                <Eye size={17} /> Visualizar
              </button>
              <button
                type='button'
                className='fechamentos-button secondary'
                onClick={deactivate}
                disabled={!!activatingId}
              >
                {activatingId === active.id && <Loader2 size={15} className='fechamentos-spin' />}
                Remover ativo
              </button>
            </div>
          </div>
        ) : (
          <p className='fechamentos-active-empty'>
            {loading
              ? 'Carregando...'
              : 'Nenhum fechamento ativo. Use “Tornar ativo” em um dos fechamentos abaixo.'}
          </p>
        )}
      </section>
      <section aria-labelledby='fechamentos-history-title'>
        <div className='fechamentos-history-heading'>
          <h2 id='fechamentos-history-title'>
            Fechamentos enviados <span>{total}</span>
          </h2>
          <div className='fechamentos-search'>
            <Search size={18} />
            <input
              type='search'
              aria-label='Buscar fechamentos'
              placeholder='Buscar pelo título ou arquivo'
              maxLength={180}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setOffset(0)
              }}
            />
          </div>
        </div>
        {listError && (
          <div className='fechamentos-message error' role='alert'>
            <AlertCircle size={18} />
            {listError}
            <button type='button' onClick={() => setRefresh((value) => value + 1)}>
              Tentar novamente
            </button>
          </div>
        )}
        {loading ? (
          <div className='fechamentos-empty' role='status'>
            <Loader2 size={30} className='fechamentos-spin' />
            <p>Carregando fechamentos...</p>
          </div>
        ) : !items.length && !listError ? (
          <div className='fechamentos-empty'>
            <FolderOpen size={42} />
            <h3>
              {searchTerm ? 'Nenhum fechamento encontrado' : 'Seu primeiro fechamento começa aqui'}
            </h3>
            <p>
              {searchTerm
                ? 'Tente buscar por outro título.'
                : 'Envie um PDF para visualizar e exportar suas páginas.'}
            </p>
          </div>
        ) : (
          <div className='fechamentos-grid'>
            {items.map((closing) => (
              <article
                className={'fechamentos-card' + (closing.isActive ? ' active' : '')}
                key={closing.id}
              >
                <button
                  type='button'
                  className='fechamentos-cover'
                  onClick={() => setViewer(closing)}
                  aria-label={'Visualizar ' + closing.title}
                >
                  <FechamentoCover closing={closing} />
                  {closing.isActive && (
                    <span className='fechamentos-active-badge'>
                      <Star size={12} /> Ativo
                    </span>
                  )}
                  <span>
                    {closing.pageCount} {closing.pageCount === 1 ? 'página' : 'páginas'}
                  </span>
                </button>
                <div className='fechamentos-card-body'>
                  <h3 title={closing.title}>{closing.title}</h3>
                  <p className='fechamentos-filename' title={closing.fileName}>
                    {closing.fileName}
                  </p>
                  <p className='fechamentos-card-meta'>
                    {formatDate(closing.createdAt)} · {formatSize(closing.fileSize)}
                  </p>
                  <p className='fechamentos-author' title={closing.createdByName}>
                    Enviado por {closing.createdByName}
                  </p>
                  <div className='fechamentos-card-actions'>
                    <button
                      type='button'
                      className='fechamentos-button primary'
                      onClick={() => setViewer(closing)}
                    >
                      <Eye size={17} /> Visualizador
                    </button>
                    <button
                      type='button'
                      className='fechamentos-button secondary'
                      onClick={() => downloadPdf(closing)}
                      disabled={!!downloadingId}
                      aria-label={'Baixar PDF de ' + closing.title}
                    >
                      {downloadingId === closing.id ? (
                        <Loader2 size={17} className='fechamentos-spin' />
                      ) : (
                        <Download size={17} />
                      )}{' '}
                      PDF
                    </button>
                  </div>
                  {closing.isActive ? (
                    <p className='fechamentos-card-active'>
                      <Star size={14} /> Fechamento ativo
                    </p>
                  ) : (
                    <button
                      type='button'
                      className='fechamentos-activate'
                      onClick={() => activate(closing)}
                      disabled={!!activatingId}
                      aria-label={'Tornar ativo ' + closing.title}
                    >
                      {activatingId === closing.id ? (
                        <Loader2 size={14} className='fechamentos-spin' />
                      ) : (
                        <Star size={14} />
                      )}
                      Tornar ativo
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
        {total > 12 && (
          <nav className='fechamentos-pagination' aria-label='Paginação dos fechamentos'>
            <button
              type='button'
              className='fechamentos-button secondary'
              disabled={offset === 0 || loading}
              onClick={() => setOffset((value) => Math.max(0, value - 12))}
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span>
              Página {Math.floor(offset / 12) + 1} de {Math.ceil(total / 12)}
            </span>
            <button
              type='button'
              className='fechamentos-button secondary'
              disabled={offset + 12 >= total || loading}
              onClick={() => setOffset((value) => value + 12)}
            >
              Próxima <ChevronRight size={16} />
            </button>
          </nav>
        )}
      </section>
      {viewer && (
        <FechamentoViewer key={viewer.id} closing={viewer} onClose={() => setViewer(null)} />
      )}
    </div>
  )
}

export default function FechamentosPage() {
  const permissions = GetPermissions()
  if (!permissions?.length) return <TopBarProgress />
  if (!permissions.some((permission) => permission.name === FECHAMENTOS_PERMISSION))
    return <Navigate to='/error/404' replace />
  return <FechamentosContent />
}
